import React from "react";
import { Card } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import stepsBack from "@assets/images/stepsBack.jpg";
import Slider from "react-slick";
import { useTranslation } from "react-i18next";
import LoaderProgressBar from "./LoadingProgressBar";
import { useSocket } from "context/SocketContext";
import { toast } from "react-toastify";
import { useUI } from "context/LoadingContext";

const ReacreationLoading = ({
  recreateLoading = false,
  timeLeft = 0,
  minuteSet = 60,
  data = null,
  loaderHeading = null,
  loaderSubheading = null,
  type = "quiz",
  onComplete = null,
  onFailed = null,
  stopCountdown = null,
  setTimeLeft,
}) => {
  const {
    sleepCoachProgress,
    mindFullnessProgress,
    clearSleepCoachProgress,
    clearMindFullnessProgress,
  } = useSocket();

  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef();
  const { t } = useTranslation();
  const { setIsActionLocked } = useUI();
 
  const progress =
    timeLeft <= 0
      ? 100
      : timeLeft <= 5
      ? 99
      : ((minuteSet - timeLeft) / minuteSet) * 100;

  // Handle socket events for sleep coach and mindfulness
  useEffect(() => {
    if (type === "sleep" && sleepCoachProgress) {
      handleProgressUpdate(sleepCoachProgress, clearSleepCoachProgress);
    }
    if (type === "mindfulness" && mindFullnessProgress) {
      handleProgressUpdate(mindFullnessProgress, clearMindFullnessProgress);
    }
  }, [sleepCoachProgress, mindFullnessProgress]);

  const handleProgressUpdate = (progressData, clearProgress) => {
    if (progressData.progress === 100) {
      setTimeLeft(0);
      setTimeout(() => {
        onComplete();
        toast.info(`${t(type)} ${t("Plan created succesfully!")}"`);
        stopCountdown();
        clearProgress();
      }, 2000);
    }

    if (progressData.progress === 0 && progressData.status === "failed") {
      if (onFailed) onFailed();
      stopCountdown();
      clearProgress();
      // toast.error(progressData?.error || `Your ${type} plan generation failed`);
    }
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 10000,
    pauseOnHover: true,
    arrows: false,

    afterChange: (index) => {
      setCurrentSlide(index);
    },

    cssEase: "cubic-bezier(0.645, 0.045, 0.355, 1)",

    appendDots: (dots) => (
      <div>
        <ul
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "8px",
            listStyle: "none",
            margin: 0,
            padding: 0,
          }}
        >
          {dots.map((dot, index) => (
            <li
              key={index}
              className={index === currentSlide ? "slick-active" : ""}
              style={{
                margin: 0,
                padding: 0,
                width: "12px",
                height: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {React.cloneElement(dot, {
                children: React.cloneElement(dot.props.children, {
                  className: index === currentSlide ? "active-dot" : "",
                }),
              })}
            </li>
          ))}
        </ul>
      </div>
    ),

    customPaging: (i) => (
      <div
        style={{
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          backgroundColor: i === currentSlide ? "#0575E6" : "#fff",
          transition: "background-color 0.3s ease",
          cursor: "pointer",
        }}
      />
    ),
  };

  useEffect(() => {
    setIsActionLocked(true);
    return () => {
      setIsActionLocked(false);
    };
  }, []);

  return (
    <>
      <div className="flex justify-center items-start m-0">
        <Card
          className="px-6 pb-10 pt-6 md:p-6 md:pb-10 md:p-12 rounded-md w-full mb-5 bg-cover bg-center"
          style={{ backgroundImage: `url(${stepsBack})` }}
        >
          <LoaderProgressBar
            progress={progress}
            loaderHeading={loaderHeading}
            loaderSubheading={loaderSubheading}
            analyzingPlan={data}
            currentSlide={currentSlide}
            type={type}
          />
          {
            <Slider
              ref={sliderRef}
              {...settings}
              // The class condition looks slightly off, ensure data is an array before mapping
              className={`max-w-[80vw] md:max-w-[unset] ${
                recreateLoading && "!hidden"
              } `}
            >
              {data &&
                data.map((item, index) => {
                  return (
                    <div
                      // Removed extra class that was causing issues
                      className="rounded-lg !grid md:!grid-cols-2 min-h-[410px] xl:min-h-[458px] p-3 md:p-0"
                      key={index}
                    >
                      <div className="flex w-full rounded-md overflow-hidden relative">
                        {/* Placeholder div */}
                        <div className="w-[50%] hidden md:block"></div>

                        <img
                          className={`rounded-md shadow-lg relative md:absolute md:left-[15%] top-[50%] w-full md:w-[80%] md:min-h-[300px] object-cover max-h-[371px]  object-top`}
                          style={{
                            transform: "translateY(-50%)",
                          }}
                          src={item?.src}
                        />
                      </div>
                      <div className="flex items-center md:px-[20px]">
                        <div className="w-full">
                          <h1 className="text-[16px] lg:text-[20px] xl:text-[28px] font-[700] text-[#fff] font-[Lato] mb-3 lg:mb-4 break-words hyphens-auto">
                            {item?.heading}
                          </h1>
                          <h2 className="text-[#fff] font-[400] font-[Lato] text-[12px] lg:text-[16px] xl:text-[20px] mb-3 lg:mb-6 break-words hyphens-auto">
                            {item?.subheading}
                          </h2>
                          <p className="text-[12px] xl:text-[18px] font-[400] text-[#fff] font-[Lato] break-words hyphens-auto leading-relaxed">
                            {item?.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </Slider>
          }
        </Card>
      </div>
    </>
  );
};
export default ReacreationLoading;
